import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import Select from 'layout/molecules/Select/Select';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { fetchProductContinents, selectProductContinents, selectProductContinentsStatus, } from 'store/parameters/productContinentsSlice';
import { addProductCountry, selectProductCountryById, updateProductCountry, } from 'store/parameters/productCountriesSlice';
import useCountryValidation from './useCountryValidation';
function CountryEditor({ create = false }) {
    var _a;
    const dispatch = useDispatch();
    const { t } = useTranslation(['specification', 'common']);
    const countryId = (_a = useSelector(selectModalByName(create ? 'countryCreator' : 'countryEditor'))) === null || _a === void 0 ? void 0 : _a.id;
    const countryDetails = useSelector((state) => selectProductCountryById(state, countryId || '')) || null;
    const continents = useSelector(selectProductContinents);
    const continentsStatus = useSelector(selectProductContinentsStatus);
    const [formValues, setFormValues] = useState({
        name: '',
        continentId: '',
    });
    const { errors, isValid, pendingFields, markTouched, validateAll } = useCountryValidation(formValues, t);
    useEffect(() => {
        if (continentsStatus.fetch === 'idle') {
            dispatch(fetchProductContinents());
        }
    }, [dispatch, continentsStatus.fetch]);
    useEffect(() => {
        var _a;
        if (!create && countryDetails) {
            setFormValues({
                name: countryDetails.name,
                continentId: ((_a = countryDetails.continent) === null || _a === void 0 ? void 0 : _a.id) || '',
            });
        }
    }, [create, countryDetails, countryId]);
    const handleNameChange = (e) => {
        setFormValues((v) => (Object.assign(Object.assign({}, v), { name: e.target.value })));
    };
    const handleContinentChange = (value) => {
        setFormValues((v) => (Object.assign(Object.assign({}, v), { continentId: value })));
    };
    const handleCancel = () => {
        dispatch(closeModal({ name: create ? 'countryCreator' : 'countryEditor' }));
    };
    const handleFormSubmit = () => {
        if (!validateAll())
            return;
        if (create) {
            dispatch(addProductCountry({
                name: formValues.name,
                continentId: formValues.continentId || null,
            }));
        }
        else {
            dispatch(updateProductCountry({
                id: countryDetails.id,
                name: formValues.name,
                continentId: formValues.continentId || null,
            }));
        }
        dispatch(closeModal({ name: create ? 'countryCreator' : 'countryEditor' }));
    };
    const continentOptions = [
        { value: '', label: `${t('common:none')}` },
        ...continents.map((c) => ({ value: c.id, label: c.name })),
    ];
    return (React.createElement(Modal, { name: create ? 'countryCreator' : 'countryEditor', size: "s", header: React.createElement(Label, { weight: "strong" }, create ? t('specification:add_country') : t('specification:edit_country')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: false, pendingFields: pendingFields, onSave: handleFormSubmit, onCancel: handleCancel, validateAll: validateAll }), onClose: handleCancel },
        React.createElement(Grid, null,
            React.createElement(FormField, { label: t('common:name'), error: errors.name },
                React.createElement(Input, { value: formValues.name, onChange: handleNameChange, onBlur: () => markTouched('name'), name: "name", error: Boolean(errors.name), autoFocus: true })),
            React.createElement(FormField, { label: t('specification:continent') },
                React.createElement(Select, { options: continentOptions, value: formValues.continentId, onChange: handleContinentChange, placeholder: t('specification:select_continent'), loading: continentsStatus.fetch === 'loading', fullWidth: true })))));
}
export default memo(CountryEditor);
